import type { Sort } from '@/stores/sorting';
import type { Company, CompanyRequest, CompanyResponse } from '@/types';
import type {
    CompanyBranding,
    CompanySettings,
    CompanySettingsPayload,
    CompanySettingsPopulators,
    ConnectPaymentProviderResponse,
    DisconnectPaymentProviderResponse,
} from '@/types/company.ts';
import { sortToQuery } from '@/utils/misc.ts';
import type { AxiosInstance, AxiosResponse } from 'axios';

class CompanyService {
    private readonly apiService;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    getPath(id?: string) {
        return id ? `/api/companies/${id}` : '/api/companies';
    }

    index(page: number, filter?: object, sort: null | Sort = null, perPage?: number) {
        return this.apiService.get(this.getPath(), {
            params: { page: page, per_page: perPage, ...filter, ...sortToQuery(sort) },
        });
    }

    show(id: string) {
        return this.apiService.get(this.getPath(id));
    }

    store(request: CompanyRequest) {
        return this.apiService.post(this.getPath(), request);
    }

    update(id: string, request: CompanyRequest): Promise<AxiosResponse<CompanyResponse>> {
        return this.apiService.put(this.getPath(id), request);
    }

    delete(id: string) {
        return this.apiService.delete(this.getPath(id));
    }

    settings(id: string): Promise<AxiosResponse<{ data: CompanySettings }>> {
        return this.apiService.get(`${this.getPath(id)}/settings`);
    }

    connectPaymentProvider(id: string): Promise<AxiosResponse<ConnectPaymentProviderResponse>> {
        return this.apiService.post(`${this.getPath(id)}/mollie/connect`);
    }

    disconnectPaymentProvider(id: string): Promise<AxiosResponse<DisconnectPaymentProviderResponse>> {
        return this.apiService.delete(`${this.getPath(id)}/mollie`);
    }

    uploadLogo(id: string, file: File): Promise<AxiosResponse<{ data: Company }>> {
        const formData = new FormData();
        formData.append('logo', file);

        return this.apiService.post(`${this.getPath(id)}/settings/logo`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    deleteLogo(id: string): Promise<AxiosResponse<{ data: Company }>> {
        // Send nullified logo to delete
        return this.apiService.post(`${this.getPath(id)}/settings/logo`, { logo: null });
    }

    // ─── Terms & conditions (MILO-352) ───────────────────────────────────────

    uploadTerms(id: string, file: File): Promise<AxiosResponse<{ data: CompanySettings }>> {
        const formData = new FormData();
        formData.append('terms', file);

        return this.apiService.post(`${this.getPath(id)}/settings/terms`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    deleteTerms(id: string): Promise<AxiosResponse<{ data: CompanySettings }>> {
        return this.apiService.delete(`${this.getPath(id)}/settings/terms`);
    }

    downloadTerms(id: string): Promise<AxiosResponse<Blob>> {
        return this.apiService.get(`${this.getPath(id)}/settings/terms`, { responseType: 'blob' });
    }

    updateSettings(
        id: string,
        settings: CompanySettingsPayload,
        updatedAt?: string | null,
    ): Promise<AxiosResponse<{ data: CompanySettings }>> {
        // Send the optimistic-lock header so RejectsStaleMutations can reject a
        // concurrent settings overwrite (otherwise the backend guard is inert).
        return this.apiService.put(`${this.getPath(id)}/settings`, settings, {
            headers: updatedAt ? { 'X-Resource-Updated-At': updatedAt } : undefined,
        });
    }

    settingsPopulators(): Promise<AxiosResponse<{ data: CompanySettingsPopulators }>> {
        return this.apiService.get(`${this.getPath()}/settings/populators`);
    }

    /** Render a sample invoice PDF for the huisstijl preview using the (unsaved) branding. */
    previewBranding(id: string, branding: CompanyBranding): Promise<AxiosResponse<Blob>> {
        return this.apiService.post(`${this.getPath(id)}/branding/preview`, { branding }, { responseType: 'blob' });
    }
}

export default CompanyService;
